import { Component, ElementRef, TemplateRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { NgbAlert, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DateTime } from 'luxon';
import { Subject, catchError, debounceTime } from 'rxjs';
import { PaymentMethodAch } from 'src/app/interfaces/payment-method-ach';
import { PaymentMethodCc } from 'src/app/interfaces/payment-method-cc';
import { PaymentMethods } from 'src/app/interfaces/payment-methods';
import { GeneralService } from 'src/app/services/general.service';
import { PaymentsService } from 'src/app/services/payments.service';
import { TranslatorService } from 'src/app/services/translator.service';

@Component({
    selector: 'app-methods',
    templateUrl: './methods.component.html',
    styleUrls: ['./methods.component.scss'],
    standalone: false
})
export class MethodsComponent {
    paymentMethods: any = {};
    activeMethods: any = {};
    previousMethodIds: number[] = [];
    totalBalance: number = 0;
    useMockData: boolean = false;
    methodCountMax: number = 5;
    isLoading = false;
    showBilling: boolean = false;
    cardTypes: string[] = ['Visa', 'MasterCard', 'American Express', 'Discover', 'Unknown'];
    displayConfirmPaymentMethodModal: boolean = false;
    displayConfirmDeleteCreditCardModal: boolean = false;
    changeDefaultCreditCard: any = {};
    changeDefaultBankAccount: any = {};
    displayDefaultPaymentMethodAccountNumber: number = 0;
    defaultPaymentMethodIdToBeChanged: number = 0;
    defaultPaymentMethodTypeToBeChanged: string = '';
    creditCardAccountIdToBeRemoved: number = 0;
    creditCardNumberDeleteDisplay: string = '';
    alertMessage: string = '';
    alertClass: string = '';
    dismissSecs: number = 15000;
    dismissCountDown: number = 0;
    newPaymentMethod: PaymentMethodCc | PaymentMethodAch = {} as PaymentMethodCc | PaymentMethodAch;
    isNewAccountAdded: boolean = false;
    billingMethodIdToUpdate: number = 0;
    isUpdateProcess: boolean = false;
    isAddOrUpdateProcess: boolean = false;
    cannotEditBillingAccountIds: number[] = [];
    iframeSrc: SafeResourceUrl | null = null;

    // Modals
    @ViewChild('ConfirmPaymentMethodModal') confirmPaymentMethodModal!: TemplateRef<any>;
    @ViewChild('ConfirmDeleteCreditCardModal') confirmDeleteCreditCardModal!: TemplateRef<any>;

    // Show alert messages
    _showDefaultChangedAlert = new Subject<boolean>();
    showDefaultChangedAlert: boolean = false;
    _showDefaultPaymentChangeFailureAlert = new Subject<boolean>();
    showDefaultPaymentChangeFailureAlert: boolean = false;
    _showRemoveCreditCardSuccess = new Subject<boolean>();
    showRemoveCreditCardSuccess: boolean = false;
    _showRemoveCreditCardError = new Subject<boolean>();
    showRemoveCreditCardError: boolean = false;
    _showAddMethodSuccess = new Subject<boolean>();
    showAddMethodSuccess: boolean = false;
    _showAddMethodError = new Subject<boolean>();
    showAddMethodError: boolean = false;
    _showUpdateMethodSuccess = new Subject<boolean>();
    showUpdateMethodSuccess: boolean = false;
    _showUpdateMethodError = new Subject<boolean>();
    showUpdateMethodError: boolean = false;

    get ShowDefaultPaymentChangeFailureAlert() {
        return this.showDefaultPaymentChangeFailureAlert;
    }

    constructor(public paymentsService: PaymentsService,
        private generalService: GeneralService,
        private translatorService: TranslatorService,
        private router: Router,
        public sanitizer: DomSanitizer,
        public modalService: NgbModal) {

        // setup alert state and auto-dismiss
        this._showDefaultChangedAlert.subscribe((value) => {
            this.showDefaultChangedAlert = value;
        });
        this._showDefaultChangedAlert.pipe(debounceTime(this.dismissSecs)).subscribe(() => {
            if (this.showDefaultChangedAlert) {
                this._showDefaultChangedAlert.next(false);
            }
        });

        this._showDefaultPaymentChangeFailureAlert.subscribe((value) => {
            this.showDefaultPaymentChangeFailureAlert = value;
        });
        this._showDefaultPaymentChangeFailureAlert.pipe(debounceTime(this.dismissSecs)).subscribe(() => {
            if (this.showDefaultPaymentChangeFailureAlert) {
                this._showDefaultPaymentChangeFailureAlert.next(false);
            }
        });

        this._showRemoveCreditCardSuccess.subscribe((value) => {
            this.showRemoveCreditCardSuccess = value;
        });
        this._showRemoveCreditCardSuccess.pipe(debounceTime(this.dismissSecs)).subscribe(() => {
            if (this.showRemoveCreditCardSuccess) {
                this._showRemoveCreditCardSuccess.next(false);
            }
        });

        this._showRemoveCreditCardError.subscribe((value) => {
            this.showRemoveCreditCardError = value;
            this.isLoading = false;
        });
        this._showRemoveCreditCardError.pipe(debounceTime(this.dismissSecs)).subscribe(() => {
            if (this.showRemoveCreditCardError) {
                this._showRemoveCreditCardError.next(false);
            }
        });

        this._showAddMethodSuccess.subscribe((value) => {
            this.showAddMethodSuccess = value;
        });
        this._showAddMethodSuccess.pipe(debounceTime(this.dismissSecs)).subscribe(() => {
            if (this.showAddMethodSuccess) {
                this._showAddMethodSuccess.next(false);
            }
        });

        this._showAddMethodError.subscribe((value) => {
            this.showAddMethodError = value;
        });
        this._showAddMethodError.pipe(debounceTime(this.dismissSecs)).subscribe(() => {
            if (this.showAddMethodError) {
                this._showAddMethodError.next(false);
            }
        });

        this._showUpdateMethodSuccess.subscribe((value) => {
            this.showUpdateMethodSuccess = value;
        });
        this._showUpdateMethodSuccess.pipe(debounceTime(this.dismissSecs)).subscribe(() => {
            if (this.showUpdateMethodSuccess) {
                this._showUpdateMethodSuccess.next(false);
            }
        });

        this._showUpdateMethodError.subscribe((value) => {
            this.showUpdateMethodError = value;
        });
        this._showUpdateMethodError.pipe(debounceTime(this.dismissSecs)).subscribe(() => {
            if (this.showUpdateMethodError) {
                this._showUpdateMethodError.next(false);
            }
        });
    }

    ngOnInit(): void {

        const component = this;

        window.onbeforeunload = null;
        this.isLoading = true;
        if (this.useMockData) {
            this.paymentMethods = this.paymentsService.mockGetBillingAccounts();
            this.loadBillingAccounts(this.paymentMethods);
            this.isLoading = false;
        } else {
            //status can be opened or closed 
            const billingAccountsPromise = this.paymentsService.getBillingAccounts();
            billingAccountsPromise.subscribe((result: PaymentMethods | any) => {
                this.loadBillingAccounts(result)
            });
        }

        window.removeEventListener('message', function (e) { });
        window.addEventListener('message', function (e) {
            if (e.data === "Cancel" && component.paymentsService.billingState != "NA") {
                component.cancelPayment();
            } else if (e.data === "PaymentAccountAdded") {
                if (component.paymentsService.billingState != "NA") {
                    component.doCallBackAction(e);
                } 
            } else if (e.data === "UpdateComplete") {
                component.paymentsService.billingState = "NA";
                component.paymentsService.walletURL = "";
                component.showBilling = false;
                component.doCallBackAction(e);
            } else if (e.data === "Timeout" || e.data === "Error") {
                component.showBillingError();
            } else if (e.data === "AddMode" && component.paymentsService.billingState !== "AddMode") {
                component.paymentsService.billingState = "AddMode";
            };
        });

        setTimeout(() => this.scrollToTop(), 500);
    }


    get activePaymentMethods() {
        return this.activeMethods;
    }

    get hasPaymentMethods() {
        return this.paymentMethods?.creditCards?.length > 0 || this.paymentMethods?.bankAccounts?.length > 0;
    }

    doCallBackAction(event: any) {
        if (event.data === "Cancel" && this.paymentsService.billingState != "NA") {
            this.cancelPayment();
        } else if (event.data === "PaymentAccountAdded") {
            if (this.paymentsService.billingState != "NA") {
                this.handleAddMethodSuccess(true);
            }
        } else if (event.data === "UpdateComplete") {
            this.paymentsService.billingState = "NA";
            this.paymentsService.walletURL = "";
            this.handleUpdateMethodSuccess(true);
        } else if (event.data === "Timeout" || event.data === "Error") {
            this.showBillingError();
        } else if (event.data === "AddMode" && this.paymentsService.billingState !== "AddMode") {
            this.paymentsService.billingState = "AddMode";
        }else if (event.data !== "" && event.data.hello == false) { // they send us back hello every few seconds
                this.showBillingError();
        }
    }


    loadBillingAccounts(billingAccounts: PaymentMethods | any) {
        if (billingAccounts) {
            this.paymentMethods = billingAccounts as PaymentMethods;
            this.activeMethods.creditCards = this.paymentMethods.creditCards.filter((item: PaymentMethodCc) => item.isActive);
            this.activeMethods.creditCards.forEach((cc: PaymentMethodCc) => {
                if (cc.address && cc.address.country && cc.address.postalCode &&
                    this.paymentsService.isUSA(cc.address.country, cc.address.postalCode) === false) {
                    this.cannotEditBillingAccountIds.push(cc.id);
                }
            });
            this.activeMethods.bankAccounts = this.paymentMethods.bankAccounts.filter((item: PaymentMethodAch) => item.isActive);
        } else {
            this.paymentMethods = [];
        }
        this.isLoading = false;
    }

    cancelPayment() {
        this.paymentsService.billingState = "NA";
        this.paymentsService.walletURL = '';
        this.showBilling = false;
        setTimeout(() => this.scrollToTop(), 500);
    }

    handleAddMethodSuccess(value: any) {
        this.showBilling = false;
        this.paymentsService.walletURL = "";
        this.paymentsService.billingState = "NA";
        if (value == true || value.Result.toString().toUpperCase() == "TRUE") {
            this._showAddMethodSuccess.next(true);
            // Store the previous set of payment methods
            this.previousMethodIds = this.getPaymentMethodIds();
            // reload billing methods
            const billingAccountsPromise = this.paymentsService.getBillingAccounts();
            billingAccountsPromise.subscribe((result: PaymentMethods | any) => {
                this.loadBillingAccounts(result);
                this.newPaymentMethod = this.getNewlyAddedPaymentMethod();
                if (this.newPaymentMethod != null) {
                    this.isNewAccountAdded = true;
                    this.isAddOrUpdateProcess = true;
                    this.showConfirmDefaultPaymentMethodModal(this.isCreditCardAccount(this.newPaymentMethod) ? 'creditCard' : 'bankAccount', this.newPaymentMethod.id);
                }
            });
        } else {
            this._showAddMethodError.next(true);
        }
        setTimeout(() => this.scrollToTop(), 500);
    }

    handleUpdateMethodSuccess(value: boolean) {
        this.showBilling = false;
        this.paymentsService.walletURL = "";
        this.paymentsService.billingState = "NA";
        this.isUpdateProcess = false;
        if (value == true || value.toString().toUpperCase() == "TRUE") {
            this._showUpdateMethodSuccess.next(true);
            this.isNewAccountAdded = false;
            const billingAccountsPromise = this.paymentsService.getBillingAccounts();
            billingAccountsPromise.subscribe((result: PaymentMethods | any) => {
                this.loadBillingAccounts(result);
                const updatedPaymentMethod = this.activePaymentMethods.creditCards.find((item: PaymentMethodCc) => item.id == this.billingMethodIdToUpdate);
                if (!updatedPaymentMethod.isDefault) {
                    this.isAddOrUpdateProcess = true;
                    this.showConfirmDefaultPaymentMethodModal('creditCard', updatedPaymentMethod.id);
                } else {
                    this.paymentsService.sendPaymentMethodAddOrUpdateEmail(updatedPaymentMethod.id, true).subscribe((resp: any) => {});
                }
            });
        } else {
            this._showUpdateMethodError.next(true);
        }
        setTimeout(() => this.scrollToTop(), 500);
    }

    getPaymentMethodIds() {
        const ids: number[] = [];
        for (const key in this.activeMethods.creditCards) {
            ids.push(this.activeMethods.creditCards[key].id);
        }
        for (const key in this.activeMethods.bankAccounts) {
            ids.push(this.activeMethods.bankAccounts[key].id);
        }
        return ids;
    }

    getNewlyAddedPaymentMethod(): any {
        let newPaymentMethod: PaymentMethodCc | PaymentMethodAch = {} as PaymentMethodCc | PaymentMethodAch;
        const currentPaymentMethodIds = this.getPaymentMethodIds();
        const previousPaymentMethodIds = this.previousMethodIds;
        const newPaymentMethodId = currentPaymentMethodIds.filter(id => !previousPaymentMethodIds.includes(id));
        if (newPaymentMethodId.length > 0) {
            for (const key in this.activeMethods.creditCards) {
                if (this.activeMethods.creditCards[key].id == newPaymentMethodId[0]) {
                    newPaymentMethod = this.activeMethods.creditCards[key];
                    break;
                }
            }
            for (const key in this.activeMethods.bankAccounts) {
                if (this.activeMethods.bankAccounts[key].id == newPaymentMethodId[0]) {
                    newPaymentMethod = this.activeMethods.bankAccounts[key];
                    break;
                }
            }
        }
        return newPaymentMethod;
    }

    showBillingError() {
        this.showBilling = false;
        this.paymentsService.walletURL = "";
        this.paymentsService.billingState = "NA";
        if (this.isUpdateProcess) {
            this._showUpdateMethodError.next(true);
            this.isUpdateProcess = false;
        } else {
            this._showAddMethodError.next(true);
        }

        setTimeout(() => this.scrollToTop(), 500);
    };

    isCreditCardExpired(creditCard: any) {
        if (creditCard.creditCardExpire) {
            const expiryDate = DateTime.fromFormat(creditCard.creditCardExpire, 'MM/yyyy').endOf('month'); // good to the end of the month.
            return DateTime.now() > expiryDate;
        }
        return false;
    }

    showConfirmDefaultPaymentMethodModal(methodType: string, paymentMethodId: number) {
        this.defaultPaymentMethodIdToBeChanged = paymentMethodId;
        this.defaultPaymentMethodTypeToBeChanged = methodType;
        if (methodType === 'creditCard') {
            this.displayDefaultPaymentMethodAccountNumber = this.paymentMethods.creditCards.filter((item: PaymentMethodCc) => item.id === paymentMethodId)[0].creditCardNumber;
        } else {
            this.displayDefaultPaymentMethodAccountNumber = this.paymentMethods.bankAccounts.filter((item: PaymentMethodAch) => item.id === paymentMethodId)[0].acctNo;
        }
        this.modalService.open(this.confirmPaymentMethodModal, { ariaLabelledBy: 'confirmPaymentMethodModalTitle' });
        this.displayConfirmPaymentMethodModal = true;
    }

    changeDefaultPaymentMethod() {
        this.modalService.dismissAll();
        this.displayConfirmPaymentMethodModal = false;
        this.isLoading = true;
        //accountType ("Check":default,"Credit Card" : "ACH")
        const accountType = this.defaultPaymentMethodTypeToBeChanged === 'creditCard' ? 'Credit Card' : 'ACH';
        const makeDefaultMethodPromise = this.paymentsService.setDefaultPaymentMethod(this.defaultPaymentMethodIdToBeChanged, accountType, this.isNewAccountAdded);
        makeDefaultMethodPromise.subscribe((result: boolean | any) => {
            this.isLoading = false;
            if (result) {
                this._showDefaultChangedAlert.next(true);
                if (accountType == 'ACH')
                    this.defaultPaymentMethodTypeToBeChanged = 'bankAccount';

                // reload billing methods
                const billingAccountsPromise = this.paymentsService.getBillingAccounts();
                billingAccountsPromise.subscribe((result: PaymentMethods | any) => this.loadBillingAccounts(result));
                //this.resetMethodsCollection();
                this.isNewAccountAdded = false;
                this.isAddOrUpdateProcess = false;
            }
            else {
                this._showDefaultPaymentChangeFailureAlert.next(true);
                this.isNewAccountAdded = false;
                this.isAddOrUpdateProcess = false;
            }
        });
    }

    cancelDefaultPaymentMethodChange() {
        this.displayConfirmPaymentMethodModal = false;
        this.modalService.dismissAll();
        if (this.isAddOrUpdateProcess) {
            this.paymentsService.sendPaymentMethodAddOrUpdateEmail(this.defaultPaymentMethodIdToBeChanged, !this.isNewAccountAdded).subscribe((resp: any) => {
                this.isNewAccountAdded = false;
                this.isAddOrUpdateProcess = false;
            });
        }
    }

    isCreditCardAccount(account: PaymentMethodCc | PaymentMethodAch): account is PaymentMethodCc {
        return (account as PaymentMethodCc).creditCardExpire !== undefined;
    }


    showConfirmDeleteCreditCardModal(paymentMethodId: number) {
        this.creditCardAccountIdToBeRemoved = paymentMethodId;
        this.creditCardNumberDeleteDisplay = this.paymentMethods.creditCards.filter((item: PaymentMethodCc) => item.id === paymentMethodId)[0].creditCardNumber;

        this.displayConfirmDeleteCreditCardModal = true;
        this.modalService.open(this.confirmDeleteCreditCardModal, { ariaLabelledBy: 'confirmDeleteCreditCardModalTitle' });
    }

    closeConfirmDeleteCreditCardModal() {
        this.modalService.dismissAll();
    }

    removeCreditCard() {
        this.modalService.dismissAll();
        this.displayConfirmDeleteCreditCardModal = false;
        this.isLoading = true;
        const removeCreditCardPromise = this.paymentsService.removeCreditCard(this.creditCardAccountIdToBeRemoved);
        removeCreditCardPromise.subscribe((result: boolean | any) => {
            if (result) {
                this._showRemoveCreditCardSuccess.next(true);
                // reload billing methods
                const billingAccountsPromise = this.paymentsService.getBillingAccounts();
                billingAccountsPromise.subscribe((result: PaymentMethods | any) => this.loadBillingAccounts(result));
            }
            else {
                this._showRemoveCreditCardError.next(true);
                this.isLoading = false;
            }
        },
            (error: any) => {
                this._showRemoveCreditCardError.next(true);
                this.isLoading = false;
            });

        setTimeout(() => this.scrollToTop(), 500);
    }

    showBillingBox() {
        this.isLoading = true;
        const lang = this.translatorService.getActiveLanguage();
        const cdbInfo = this.paymentsService.createCDBBillingAccount(lang);
        cdbInfo.subscribe((v: any) => this.cdbInfo(v));
        setTimeout(() => this.scrollToTop(), 500);
    }

    cdbInfo(response: any) {
        if (response === '') {
            setTimeout(() => this.scrollToTop(), 500);
            this.router.navigateByUrl('/payment');
        } else {
            this.setIframeInfo(response);
        }
    }

    setIframeInfo(data: any) {
        this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(data);
        this.paymentsService.walletURL = data;
        this.paymentsService.billingState = "get";
        this.showBilling = true;
        this.isLoading = false;
    }

    allowCreditCardEdit(billingMethodId: number) {
        return this.cannotEditBillingAccountIds.indexOf(billingMethodId) === -1;
    }

    scrollToTop() {
        window.scrollTo(0, 0);
    }

    showUpdateBillingBox(creditCard: PaymentMethodCc) {
        if (this.cannotEditBillingAccountIds.indexOf(creditCard.id) > -1) {
            console.error('User attempted edit of credit card with address not in USA. Operation prohibited.');
            return;
        }
        setTimeout(() => this.scrollToTop(), 500);
        this.isUpdateProcess = true;
        this.billingMethodIdToUpdate = creditCard.id;
        this.isLoading = true;
        const lang = this.translatorService.getActiveLanguage();
        const cdbInfo = this.paymentsService.editCDBCreditCard(creditCard, lang);
        cdbInfo.subscribe((v: any) => { this.cdbUpdateInfo(v) });
    }

    cdbUpdateInfo(response: any) {
        if (response && response.length > 0) {
            if (response === 'error') {
                this._showUpdateMethodError.next(true);
                setTimeout(() => this.scrollToTop(), 500);
                this.isLoading = false;
            } else {
                this.setUpdateIframeInfo(response);
            }

        } else {
            this.router.navigateByUrl('/payment');
        }
    }

    setUpdateIframeInfo(data: any) {
        this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(data);
        this.paymentsService.walletURL = data;
        this.paymentsService.billingState = "get";
        this.showBilling = true;
        this.isLoading = false;
    }

}
